import { findIndex, isEmpty, isEqual, range } from 'lodash';

import { RenderEngine } from '../models/render-engine';
import { AddressConverter } from '../../address-converter';
import { Block } from '../models/block';
import { Styles } from '../models/styles';
import Tools from '../../tools';
import SpacerBlock from '../blocks/calendar/spacer-block';
import FlightBarWallSubTotalBlock from '../blocks/media-hierarchy/flight-bar-wall-sub-total-block';
import ValueBlock from '../blocks/right-hand-totals/value-block';
import FlightBarWallBarBlock from '../blocks/media-hierarchy/flight-bar-wall-bar-block';
import FlightBarWallInflightOverlayBlock from '../blocks/media-hierarchy/flight-bar-wall-inflight-overlay-block';
import TotalBlock from '../blocks/right-hand-totals/total-block';
import GTValueBlock from '../blocks/grand-totals/value-block';
import { inferExcelNumberFormat } from '../helpers/excel-format-helper';
export type CombinedStyle = {
    styles: Styles;
    ranges: string[];
};

export class ExcelRenderer extends RenderEngine {
    ac = new AddressConverter();
    isGettingRendered: boolean = false;

    setupWorksheet = async (
        context: Excel.RequestContext,
        name: string = 'Report',
        deleteSheet: boolean = false
    ): Promise<Excel.Worksheet> => {
        let app = context.workbook.application;
        let sheet = context.workbook.worksheets.getItemOrNullObject(name);

        await context.sync();

        if (sheet.isNullObject) {
            sheet = context.workbook.worksheets.add(name);
        }

        if (deleteSheet) {
            sheet.delete();
            sheet = context.workbook.worksheets.add(name);
        }

        await context.sync();

        sheet.activate();
        app.suspendScreenUpdatingUntilNextSync();

        return sheet;
    };

    setupStylesheet = async (
        context: Excel.RequestContext,
        name: string = 'Styles'
    ): Promise<Excel.Worksheet> => {
        let app = context.workbook.application;
        let sheet = context.workbook.worksheets.getItemOrNullObject(name);

        await context.sync();

        if (sheet.isNullObject) {
            sheet = context.workbook.worksheets.add(name);
        }

        sheet.visibility = Excel.SheetVisibility.hidden;

        app.suspendScreenUpdatingUntilNextSync();

        return sheet;
    };

    render = async (
        blockRows: Block[][],
        {
            name,
            levelName,
            columnStart = 0,
            rowStart = 0,
            deleteSheet,
            weekly,
            precisedValue,
            isDecimal,
            setCurrentExcelContext,
            onWorkSheetFormatChanged
        }: {
            name?: string;
            levelName?: string;
            columnStart?: number;
            rowStart?: number;
            deleteSheet?: boolean;
            weekly?: boolean;
            precisedValue?: boolean;
            isDecimal?: boolean;
            setCurrentExcelContext?: (x: Excel.RequestContext) => void;
            onWorkSheetFormatChanged?: (event: Excel.WorksheetFormatChangedEventArgs) => Promise<any>;
        } = {}
    ) => {
        if (this.isGettingRendered) {
            return;
        }

        const value = sessionStorage.getItem('isGettingRendered');
        if (value && value === "true") {
            return;
        }

        this.isGettingRendered = true;

        try {
            let combinedStyles: CombinedStyle[] = [];
            let combinedValueStyles: CombinedStyle[] = [];
            let mergeRanges = [];
            await Excel.run(async (context) => {
                const numberFormatRanges = new Map<string, { format: string, length: number }>();
                const worksheet = await this.setupWorksheet(
                    context,
                    name,
                    deleteSheet
                );
                worksheet.onFormatChanged.remove(onWorkSheetFormatChanged);
                const stylesheet = await this.setupStylesheet(context);

                let imageData = [];
                const autofitColumnRanges: string[] = [];
                const autofitRowRanges: string[] = [];
                const valueWrites: { col: number; row: number; values: any[][] }[] = [];
                let currentRow = rowStart;
                const currentColumnRow: {
                    [key: string]: { value: number; touched: boolean };
                } = {};
                blockRows.map((rows, rowIndex) => {
                    currentRow += 1;

                    let currentColumn = columnStart;
                    rows.map(async (block, columnIndex) => {
                        const {
                            columnStart: blockColumnStart,
                            rowStart: blockRowStart,
                            columnLength,
                            rowLength,
                            columnAfter,
                            rowAfter,
                            merge,
                            containsImage,
                            imageMaxHeight,
                            autofitColumns,
                        } = block;
                        currentColumn += 1;

                        // Set current column row index
                        const currentColumnRowIndex =
                            currentColumn + blockColumnStart;

                        // Set minimum column row
                        let minColumnRow;
                        Array.from({ length: columnLength }).forEach(
                            (_c, columnIndex) => {
                                const finalColumnRowIndex =
                                    currentColumnRowIndex + columnIndex;

                                if (
                                    currentColumnRow[finalColumnRowIndex] !==
                                    undefined &&
                                    (minColumnRow === undefined ||
                                        currentColumnRow[finalColumnRowIndex]
                                            .value > minColumnRow)
                                ) {
                                    minColumnRow =
                                        currentColumnRow[finalColumnRowIndex].value;
                                }
                            }
                        );

                        // Set current column row start
                        Array.from({ length: columnLength }).forEach(
                            (_c, columnIndex) => {
                                const finalColumnRowIndex =
                                    currentColumnRowIndex + columnIndex;

                                if (
                                    currentColumnRow[finalColumnRowIndex] ===
                                    undefined
                                ) {
                                    currentColumnRow[finalColumnRowIndex] = {
                                        value:
                                            minColumnRow !== undefined
                                                ? minColumnRow + blockRowStart
                                                : blockRowStart,
                                        touched: true,
                                    };
                                } else {
                                    if (minColumnRow !== undefined) {
                                        currentColumnRow[
                                            finalColumnRowIndex
                                        ].value = minColumnRow;
                                    }
                                    currentColumnRow[finalColumnRowIndex].value +=
                                        blockRowStart;
                                    currentColumnRow[finalColumnRowIndex].touched =
                                        true;
                                }
                            }
                        );

                        // Get range address
                        const startColCoord = currentColumn + blockColumnStart;
                        const startRowCoord =
                            currentRow +
                            currentColumnRow[currentColumnRowIndex].value;
                        const { text: rangeStart } = this.ac.getAddressFromCoords(
                            startColCoord,
                            startRowCoord
                        );
                        currentColumn += blockColumnStart + columnLength - 1;
                        const { text: rangeEnd } = this.ac.getAddressFromCoords(
                            currentColumn,
                            currentRow +
                            currentColumnRow[currentColumnRowIndex].value +
                            rowLength -
                            1
                        );
                        const rangeAddress = `${rangeStart}:${rangeEnd}`;

                        // Get value data
                        // Todo SG: Check isSame
                        const { isEmpty, isSame, values } =
                            this.getValueData(block);

                        // Get styles
                        // Todo SG: Check style implementation

                        // Merge style from general to specific definition.
                        if (!(block instanceof SpacerBlock)) {
                            const generalStyle = Tools.deepCopy(
                                block.getGeneralStyles()
                            );
                            const componentStyle = Tools.deepCopy(
                                block.getComponentStyles()
                            );
                            const directStyles = Tools.deepCopy(
                                block.getDirectStyles()
                            );
                            const legendStyles = Tools.deepCopy(
                                block.getLegendStyles()
                            );

                            let mergedStyle = {};

                            mergedStyle = Tools.mergeObjects(
                                generalStyle,
                                componentStyle
                            );

                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                legendStyles
                            );

                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                directStyles
                            );
                            const metricBlock = (block instanceof FlightBarWallSubTotalBlock) || (block instanceof ValueBlock) || (block instanceof FlightBarWallBarBlock);
                            let styles = precisedValue || isDecimal || !metricBlock ? block.getStyles() : {
                                ...block.getStyles(),
                                columnWidth: block instanceof ValueBlock ?
                                    Math.max(
                                        block.value?.toString().length * 12,  // Increased multiplier for better spacing
                                        (block.getValue()?.toString() || '').length * 12  // Account for actual values
                                    ) : 50
                            };
                            mergedStyle = Tools.mergeObjects(
                                mergedStyle,
                                styles,
                            );

                            block.styles = mergedStyle;
                        }


                        // commented as casuing issue in formatting
                        if (!isEmpty) {
                            combinedValueStyles = this.combineStyles(
                                block,
                                rangeAddress,
                                combinedValueStyles
                            );
                        } else {
                            combinedStyles = this.combineStyles(
                                block,
                                rangeAddress,
                                combinedStyles
                            );
                        }

                        // Mark block for merge if needed
                        if (merge && (columnLength > 1 || rowLength > 1)) {
                            mergeRanges.push(rangeAddress);
                        }

                        // Only render values, if not empty
                        if (!isEmpty) {
                            // Set values
                            if (!containsImage) {
                                valueWrites.push({
                                    col: startColCoord,
                                    row: startRowCoord,
                                    values,
                                });
                                const isMetricBlock = block instanceof FlightBarWallSubTotalBlock
                                    || block instanceof ValueBlock
                                    || block instanceof FlightBarWallBarBlock
                                    || block instanceof FlightBarWallInflightOverlayBlock
                                    || block instanceof TotalBlock
                                    || block instanceof GTValueBlock;
                                if (isMetricBlock && values.length > 0 && values[0].length > 0) {
                                    try {
                                        const inferredFormat = inferExcelNumberFormat(values[0][0]);
                                        numberFormatRanges.set(rangeAddress, { format: inferredFormat, length: values.length });

                                    } catch (e) {
                                        console.log('error while fetching the formatting')
                                    }

                                }
                            } else {
                                //passing of the image content into an object
                                const { content, width, height } = JSON.parse(values[0][0]);
                                const scaledWidth = width * (1 + 1 / 100);
                                const scaledHeight = height * (1 + 1 / 100);
                                //to scale after the image is drawn
                                const img = worksheet.shapes.addImage(content);
                                img.width = scaledWidth;
                                img.height = scaledHeight;
                                const { text: imageRangeEnd } =
                                    // Todo SG + ZZ: Adapt ranges (assume that it no longer works correctly with my update)
                                    this.ac.getAddressFromCoords(
                                        currentColumn,
                                        currentRow +
                                        blockRowStart +
                                        imageMaxHeight -
                                        1
                                    );

                                const imageRangeAddress = `${rangeStart}:${imageRangeEnd}`;
                                img.load();

                                imageData.push({
                                    rangeAddress: imageRangeAddress,
                                    img,
                                });

                            }
                            // Auto fit columns (collected and applied in batches after the loop)
                            autofitColumns && autofitColumnRanges.push(rangeAddress);
                            autofitRowRanges.push(rangeAddress);
                        }

                        // Update current column and current column row
                        currentColumn += columnAfter;
                        currentColumnRow[currentColumnRowIndex].value +=
                            rowLength - 1 + rowAfter;
                    });

                    // Correct untouched columnRow values
                    Object.entries(currentColumnRow).forEach(([, columnRow]) => {
                        if (!columnRow.touched) {
                            columnRow.value -= 1;
                            if (columnRow.value < 0) columnRow.value = 0;
                        } else {
                            columnRow.touched = false;
                        }
                    });
                });

                // Write all queued block values as a single 2D array. Cells not
                // covered by any block stay null, which Excel leaves unchanged,
                // so every value keeps its original row and column.
                let minCol = Infinity;
                let minRow = Infinity;
                let maxCol = -Infinity;
                let maxRow = -Infinity;
                // Flush the queued operations after each render phase: this
                // keeps request payloads bounded on large renders, re-suspends
                // screen updating, and makes loaded properties (e.g. image
                // ranges) available to later phases.
                const flushPhase = async () => {
                    await context.sync();
                    context.application.suspendScreenUpdatingUntilNextSync();
                };
                await flushPhase();
                if (valueWrites.length) {
                    valueWrites.forEach(({ col, row, values }) => {
                        minCol = Math.min(minCol, col);
                        minRow = Math.min(minRow, row);
                        maxCol = Math.max(maxCol, col + (values[0]?.length ?? 1) - 1);
                        maxRow = Math.max(maxRow, row + values.length - 1);
                    });

                    const matrix: any[][] = Array.from(
                        { length: maxRow - minRow + 1 },
                        () => new Array(maxCol - minCol + 1).fill(null)
                    );
                    valueWrites.forEach(({ col, row, values }) => {
                        values.forEach((rowValues, r) => {
                            rowValues.forEach((value, c) => {
                                matrix[row - minRow + r][col - minCol + c] = value;
                            });
                        });
                    });

                    const valueRange = worksheet.getRangeByIndexes(
                        minRow - 1,
                        minCol - 1,
                        matrix.length,
                        matrix[0].length
                    );
                    valueRange.values = matrix;
                    valueRange.untrack();
                    await flushPhase();
                }

                // Apply queued range operations in as few RangeAreas objects as
                // possible. Batches are split by joined address string length
                // (the practical Office.js limit is on payload size, not range
                // count), so most groups resolve to a single getRanges call.
                const MAX_ADDRESS_LENGTH = 8000;
                const forEachRangeBatch = (
                    addresses: string[],
                    apply: (rangeAreas: Excel.RangeAreas) => void
                ) => {
                    if (!addresses.length) return;

                    const unique = addresses.length > 1
                        ? Array.from(new Set(addresses))
                        : addresses;

                    let batch: string[] = [];
                    let batchLength = 0;
                    const flush = () => {
                        if (!batch.length) return;
                        const rangeAreas = worksheet.getRanges(batch.join(','));
                        apply(rangeAreas);
                        rangeAreas.untrack();
                        batch = [];
                        batchLength = 0;
                    };

                    for (const address of unique) {
                        if (batchLength + address.length + 1 > MAX_ADDRESS_LENGTH) {
                            flush();
                        }
                        batch.push(address);
                        batchLength += address.length + 1;
                    }
                    flush();
                };

                // Auto fit rows/columns once, before styles set explicit sizes.
                // Rows: one contiguous range over the rendered bounding box is
                // dramatically faster than thousands of discontiguous areas.
                forEachRangeBatch(autofitColumnRanges, (rangeAreas) =>
                    rangeAreas.format.autofitColumns()
                );
                if (autofitRowRanges.length && Number.isFinite(minRow)) {
                    const autofitRange = worksheet.getRangeByIndexes(
                        minRow - 1,
                        minCol - 1,
                        maxRow - minRow + 1,
                        maxCol - minCol + 1
                    );
                    autofitRange.format.autofitRows();
                    autofitRange.untrack();
                }
                await flushPhase();

                // Apply all styles with a single setCellProperties call over the
                // bounding box of all styled ranges. This replaces the previous
                // per-style-group copyFrom over thousands of discontiguous areas,
                // which desktop Excel executes area-by-area and dominates render
                // time. Row heights and column widths are row/column properties,
                // so they are applied separately as consecutive-run range ops.
                const parseRangeAddress = (address: string) => {
                    const [startAddr, endAddr] = address.split(':');
                    const parse = (a: string) => {
                        const [column, row] = a.toUpperCase().match(/[A-Z]+|[0-9]+/g);
                        const { x, y } = this.ac.getCoordsFromAddress(column, row);
                        return { col: x, row: y };
                    };
                    const s = parse(startAddr);
                    const e = endAddr ? parse(endAddr) : s;
                    return { c1: s.col, r1: s.row, c2: e.col, r2: e.row };
                };

                const allStyleGroups = [...combinedStyles, ...combinedValueStyles]
                    .filter(({ ranges }) => ranges.length);

                if (allStyleGroups.length) {
                    let sMinCol = Infinity, sMinRow = Infinity, sMaxCol = -Infinity, sMaxRow = -Infinity;
                    const parsedGroups = allStyleGroups.map(({ styles, ranges }) => {
                        const { columnWidth, rowHeight, ...cellFormat } = (styles ?? {}) as any;
                        const parsed = ranges.map(parseRangeAddress);
                        parsed.forEach(({ c1, r1, c2, r2 }) => {
                            sMinCol = Math.min(sMinCol, c1);
                            sMinRow = Math.min(sMinRow, r1);
                            sMaxCol = Math.max(sMaxCol, c2);
                            sMaxRow = Math.max(sMaxRow, r2);
                        });
                        return { cellFormat, columnWidth, rowHeight, parsed };
                    });

                    const styleRows = sMaxRow - sMinRow + 1;
                    const styleCols = sMaxCol - sMinCol + 1;
                    const EMPTY: Excel.SettableCellProperties = {};
                    const cellPropsMatrix: Excel.SettableCellProperties[][] = Array.from(
                        { length: styleRows },
                        () => new Array(styleCols).fill(EMPTY)
                    );
                    const columnWidths = new Map<number, number>();
                    const rowHeights = new Map<number, number>();

                    parsedGroups.forEach(({ cellFormat, columnWidth, rowHeight, parsed }) => {
                        const props: Excel.SettableCellProperties = { format: cellFormat };
                        parsed.forEach(({ c1, r1, c2, r2 }) => {
                            for (let r = r1; r <= r2; r++) {
                                for (let c = c1; c <= c2; c++) {
                                    const existing = cellPropsMatrix[r - sMinRow][c - sMinCol];
                                    cellPropsMatrix[r - sMinRow][c - sMinCol] =
                                        existing === EMPTY
                                            ? props
                                            : { format: { ...existing.format, ...cellFormat } };
                                }
                            }
                            if (columnWidth != null) {
                                for (let c = c1; c <= c2; c++) columnWidths.set(c, columnWidth);
                            }
                            if (rowHeight != null) {
                                for (let r = r1; r <= r2; r++) rowHeights.set(r, rowHeight);
                            }
                        });
                    });

                    const styleRange = worksheet.getRangeByIndexes(
                        sMinRow - 1,
                        sMinCol - 1,
                        styleRows,
                        styleCols
                    );
                    styleRange.setCellProperties(cellPropsMatrix);
                    styleRange.untrack();

                    // Apply row heights / column widths in consecutive runs
                    const applyRuns = (
                        map: Map<number, number>,
                        apply: (startIndex: number, count: number, value: number) => void
                    ) => {
                        const keys = Array.from(map.keys()).sort((a, b) => a - b);
                        let i = 0;
                        while (i < keys.length) {
                            let j = i;
                            while (
                                j + 1 < keys.length &&
                                keys[j + 1] === keys[j] + 1 &&
                                map.get(keys[j + 1]) === map.get(keys[i])
                            ) {
                                j++;
                            }
                            apply(keys[i], keys[j] - keys[i] + 1, map.get(keys[i]));
                            i = j + 1;
                        }
                    };

                    applyRuns(rowHeights, (startRow, count, value) => {
                        const r = worksheet.getRangeByIndexes(startRow - 1, sMinCol - 1, count, 1);
                        r.format.rowHeight = value;
                        r.untrack();
                    });
                    applyRuns(columnWidths, (startCol, count, value) => {
                        const r = worksheet.getRangeByIndexes(sMinRow - 1, startCol - 1, 1, count);
                        r.format.columnWidth = value;
                        r.untrack();
                    });
                }
                await flushPhase();

                // Apply merges
                mergeRanges.forEach((rangeAddress) => {
                    const range = worksheet.getRange(rangeAddress);
                    range.merge(false);
                    range.untrack();
                });
                await flushPhase();

                // Apply all number formats in a single matrix write; null cells
                // are left unchanged by Excel.
                if (numberFormatRanges.size) {
                    let nMinCol = Infinity, nMinRow = Infinity, nMaxCol = -Infinity, nMaxRow = -Infinity;
                    const parsedFormats: { c1: number; r1: number; c2: number; r2: number; format: string }[] = [];
                    numberFormatRanges.forEach((format, rangeAddress) => {
                        const parsed = parseRangeAddress(rangeAddress);
                        parsedFormats.push({ ...parsed, format: format.format });
                        nMinCol = Math.min(nMinCol, parsed.c1);
                        nMinRow = Math.min(nMinRow, parsed.r1);
                        nMaxCol = Math.max(nMaxCol, parsed.c2);
                        nMaxRow = Math.max(nMaxRow, parsed.r2);
                    });

                    const formatMatrix: (string | null)[][] = Array.from(
                        { length: nMaxRow - nMinRow + 1 },
                        () => new Array(nMaxCol - nMinCol + 1).fill(null)
                    );
                    parsedFormats.forEach(({ c1, r1, c2, r2, format }) => {
                        for (let r = r1; r <= r2; r++) {
                            for (let c = c1; c <= c2; c++) {
                                formatMatrix[r - nMinRow][c - nMinCol] = format;
                            }
                        }
                    });

                    const numberFormatRange = worksheet.getRangeByIndexes(
                        nMinRow - 1,
                        nMinCol - 1,
                        formatMatrix.length,
                        formatMatrix[0].length
                    );
                    numberFormatRange.numberFormat = formatMatrix as any;
                    numberFormatRange.untrack();
                }
                await flushPhase();


                // 1. get the image range position, now that all rows and columns have their final size
                const images = imageData.map(({ img, rangeAddress }) => {
                    const imageRange = worksheet.getRange(rangeAddress);
                    imageRange.load();
                    return { img: img as Excel.Shape, range: imageRange as Excel.Range, defaultWidthPercentage: 4, defaultHeightPercentage: 4 };
                });
                // 2. ensure that (range)data can be accessed
                await flushPhase();

                // 3. scale and draw all images
                this.drawImages(images);

                setCurrentExcelContext && setCurrentExcelContext(context);
            });
        } catch (e) {
            console.log('Rendering session:', e);
        } finally {
            this.isGettingRendered = false;
            sessionStorage.setItem('isGettingRendered', "false");
        }
    };

    merge = (dst, src) => {
        Object.keys(src).forEach((key) => {
            if (!dst[key]) {
                dst[key] = src[key];
            } else if (
                typeof src[key] === 'object' &&
                src[key] !== null &&
                typeof dst[key] === 'object' &&
                dst[key] !== null
            ) {
                this.merge(dst[key], src[key]);
            }
        });
    };

    drawImages = async (
        images: {
            img: Excel.Shape;
            range: Excel.Range;
            defaultWidthPercentage: number;
            defaultHeightPercentage: number;
        }[]
    ) => {
        images.forEach(async ({ img, range, defaultWidthPercentage, defaultHeightPercentage }, index) => {
            //Interpretation of input 1 to 1% of image width and height increase 
            const scaleFactor = Math.trunc(9 + defaultWidthPercentage / 100);

            // Calculate scaled width and height
            const truncatedWidth = Math.floor(img.width);
            const scaledWidth = Math.floor(truncatedWidth * scaleFactor - 4) || defaultWidthPercentage;
            const truncatedHeight = Math.floor(img.height);
            const scaledHeight = Math.floor(truncatedHeight * scaleFactor - 4) || defaultHeightPercentage;

            // Set position for the image
            const left = range.left;
            const top = range.top;

            img.set({
                name: `image${index}`,
                left: left,
                top: top,
                width: scaledWidth,
                height: scaledHeight,
            });
        });
    };

    combineStyles = (
        block: Block,
        range: string,
        combinedStyles: CombinedStyle[] = []
    ): CombinedStyle[] => {
        const finalCombinedStyles = [...combinedStyles];

        const address = this.ac.getRangeFromString(range);
        const coords = this.ac.getCoordsFromAddress(
            address.start.column,
            address.start.row
        );

        this.getStyles(block).forEach((rows, rowIndex) => {
            // Check if all styles of the block are the same
            let isSame = true;
            let lastStyles = null;
            rows.forEach((styles) => {
                if (isSame) {
                    if (lastStyles && !isEqual(lastStyles, styles))
                        isSame = false;

                    lastStyles = styles;
                }
            });

            // If all styles of the block are the same, combine style for complete range
            if (isSame) {
                // modified find index condition for FB-291 
                let stylesIndex = findIndex(finalCombinedStyles, (combinedStyles) =>
                    combinedStyles.styles == lastStyles
                );
                if (stylesIndex === -1) {
                    finalCombinedStyles.push({
                        styles: lastStyles,
                        ranges: [range],
                    });
                    stylesIndex = finalCombinedStyles.length - 1;
                } else {
                    const existingRanges = finalCombinedStyles[stylesIndex].ranges;
                    // Avoid O(n^2) uniq(): duplicates only occur when the same
                    // range is added for consecutive rows of the same block
                    if (existingRanges[existingRanges.length - 1] !== range) {
                        existingRanges.push(range);
                    }
                }
            }
            // Otherwise, combine style for each column of the range separately
            else {
                rows.forEach((styles, columnIndex) => {
                    const { x, y } = { x: coords.x + columnIndex, y: coords.y };
                    const { text: columnAddress } =
                        this.ac.getAddressFromCoords(x, y);
                    const columnRange = `${columnAddress}:${columnAddress}`;

                    let stylesIndex = findIndex(finalCombinedStyles, {
                        styles,
                    });
                    if (stylesIndex === -1) {
                        finalCombinedStyles.push({
                            styles,
                            ranges: [columnRange],
                        });
                        stylesIndex = finalCombinedStyles.length - 1;
                    } else {
                        finalCombinedStyles[stylesIndex].ranges.push(
                            columnRange
                        );
                    }

                    // finalCombinedStyles[stylesIndex].ranges = uniq(
                    //     finalCombinedStyles[stylesIndex].ranges
                    // );
                });
            }
        });

        return finalCombinedStyles;
    };



    getAppliedStyles = async (savedStyles: Array<{ address: string, format: Excel.RangeFormat }>) => {
        // Create an array to store cell formats
        const cellFormats: Array<{ address: string, format: Excel.RangeFormat }> = savedStyles;


        const value = sessionStorage.getItem('isGettingRendered');


        this.isGettingRendered = true;

        try {


        } catch (e) {
            console.log('Rendering session:', e);
        } finally {
            this.isGettingRendered = false;
            sessionStorage.setItem('isGettingRendered', "false");
        }
        return cellFormats;

    };
    applySavedStyles = async (savedStyles: Array<{ address: string, format: Excel.RangeFormat }>) => {
        // apply styles to the sheet
        try {

            await Excel.run(async (context) => {
                let sheet = context.workbook.worksheets.getItemOrNullObject("Report");
                let app = context.workbook.application;
                await context.sync();

                if (!sheet.isNullObject) {
                    const usedRange = sheet.getUsedRangeOrNullObject();
                    await context.sync();

                    if (!usedRange.isNullObject && savedStyles.length) {
                        // Group saved styles by address and only load those cells,
                        // instead of loading every cell of the used range
                        const stylesByAddress = new Map<string, Excel.RangeFormat[]>();
                        savedStyles.forEach(({ address, format }) => {
                            const formats = stylesByAddress.get(address);
                            if (formats) {
                                formats.push(format);
                            } else {
                                stylesByAddress.set(address, [format]);
                            }
                        });

                        const propertiesToLoadInFormat = [
                            'fill', 'font', 'borders', 'columnWidth', 'horizontalAlignment',
                            'indentLevel', 'rowHeight', 'verticalAlignment', 'wrapText'
                        ];

                        const cells: { address: string; cell: Excel.Range }[] = [];
                        stylesByAddress.forEach((_formats, address) => {
                            const cell = sheet.getRange(address);
                            cell.format.load(propertiesToLoadInFormat);
                            cells.push({ address, cell });
                        });
                        await context.sync();
                        app.suspendScreenUpdatingUntilNextSync();
                        const changeBorder = (sourceBorder: Excel.RangeBorderCollection, destinationBorder: Excel.RangeBorder[], sideIndex: Excel.BorderIndex) => {
                            const border = sourceBorder.items.find((c) => c.sideIndex == sideIndex);
                            if (border) {
                                const destBorder = destinationBorder.find(x => x.sideIndex === sideIndex);
                                if (destBorder) {
                                    border.style = destBorder.style;
                                    border.weight = destBorder.weight;
                                    border.color = destBorder?.color;
                                }
                            }
                        };

                        for (const { address, cell } of cells) {
                            const cellFormat = cell.format;
                            const cellStyles = stylesByAddress.get(address);
                            if (!cellStyles) {
                                continue;
                            }
                            let cellStyle1: {};

                            cellStyles.forEach((format, index) => {
                                if (index > 0) {
                                    cellStyle1 = Object.assign(cellStyle1, format);
                                } else {
                                    cellStyle1 = format;
                                }
                            });
                            if (cellStyle1) {
                                const cellStyle = cellStyle1 as Excel.RangeFormat;
                                if (!cellStyle.fill?.isNullObject) {
                                    cellFormat.fill.color = cellStyle.fill.color;
                                }
                                if (!cellStyle.font?.isNullObject) {
                                    Object.assign(cellFormat.font, {
                                        bold: cellStyle.font?.bold,
                                        color: cellStyle.font?.color,
                                        italic: cellStyle.font?.italic,
                                        name: cellStyle.font?.name,
                                        size: cellStyle.font?.size,
                                        underline: cellStyle.font?.underline
                                    });
                                }
                                if (!cellStyle.borders?.isNullObject && !Array.isArray(cellStyle.borders)) {
                                    const borderItems = cellStyle.borders.items;
                                    if (!isEmpty(borderItems)) {
                                        for (const sideIndex of [Excel.BorderIndex.edgeBottom, Excel.BorderIndex.edgeTop, Excel.BorderIndex.edgeLeft, Excel.BorderIndex.edgeRight]) {
                                            changeBorder(cellFormat.borders, borderItems, sideIndex);
                                        }
                                    }
                                }
                                if (cellStyle?.columnWidth != null) {
                                    cellFormat.columnWidth = cellStyle.columnWidth;
                                }
                                if (cellStyle?.rowHeight != null) {
                                    cellFormat.rowHeight = cellStyle.rowHeight;
                                }
                                if (cellStyle?.indentLevel != null) {
                                    cellFormat.indentLevel = cellStyle.indentLevel;
                                }
                                if (cellStyle?.horizontalAlignment != null) {
                                    cellFormat.horizontalAlignment = cellStyle.horizontalAlignment;
                                }
                                if (cellStyle?.verticalAlignment != null) {
                                    cellFormat.verticalAlignment = cellStyle.verticalAlignment;
                                }
                                if (cellStyle?.wrapText != null) {
                                    cellFormat.wrapText = cellStyle.wrapText;
                                }
                            }
                        }
                    }
                    await context.sync();
                }
            });

        } catch (e) {
            console.log(e);
        }
    }
    setUpEvents = async (onWorkSheetFormatChanged: (event: Excel.WorksheetFormatChangedEventArgs) => Promise<any>) => {
        try {
            await Excel.run(async (context) => {
                let sheet = context.workbook.worksheets.getItemOrNullObject("Report");
                await context.sync();
                if (!sheet.isNullObject) {
                    sheet.onFormatChanged.add(onWorkSheetFormatChanged);
                    await context.sync()
                }
            });
        }
        catch (e) {
            console.log(e);
        }

    }

}
